import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { MathUtils } from 'three';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import CameraPositionSpriteModel from '../../../models/standard/CameraPositionSpriteModel';
import PanoMeasurementModel from '../../../models/standard/PanoMeasurementModel';
import PlanModel from '../../../models/standard/PlanModel';
import { retrievePanoMeasurements } from '../../../utils/apiUtils';
import { importAllMeasurementsToPlanJson } from '../../../utils/planJsonExportImportUtils';
import { convertBuildingAttributesToRegion, ERegionCode } from '../../../utils/regionUtils';
import { getSpaceUuidAtPointByGeometry } from '../../../utils/spaceUtils';
import { useAnnotations } from '../../AnnotationsProvider';
import { useDrawing } from '../../DrawingProvider';
import { usePlan } from '../../PlanProvider';
import { SnackbarType, useSnackbar } from '../../SnackbarProvider';

export interface IPanoramaMeasurementActions {
  /**
   * Fetches panorama measurements for the given plan, converts them into model instances,
   * and stores them in state.
   */
  fetchPanoramaMeasurements: (plan: PlanModel, authToken: string) => Promise<PanoMeasurementModel[]>;

  /**
   * Imports a selection of measurements and updates the scene.
   */
  handleImportMeasurements: (selectedMeasurements: PanoMeasurementModel[], overridePlan?: PlanModel) => Promise<void>;

  /** Indicates whether the measurement selection modal should be open. */
  measurementModalOpen: boolean;

  /** Setter for the measurement selection modal state. */
  setMeasurementModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  /** The current array of panorama measurements. */
  panoMeasurements: PanoMeasurementModel[];

  /** Setter for the panorama measurements state. */
  setPanoMeasurements: React.Dispatch<React.SetStateAction<PanoMeasurementModel[]>>;

  /** Whether we're currently importing/applying a measurement */
  measurementLoading: boolean;

  /** allow clearing/loading from outside */
  setMeasurementLoading: React.Dispatch<React.SetStateAction<boolean>>;

  conflictModalOpen: boolean;
  conflictingMeasurements: any[];
  handleConfirmConflict: (decision: string) => void;
  handleCancelConflict: () => void;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function deepClonePlain<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function collectUuidValues(value: any, out: Set<string>) {
  if (!value || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    value.forEach((item) => collectUuidValues(item, out));
    return;
  }

  Object.entries(value).forEach(([key, nested]) => {
    if (key === 'uuid' && typeof nested === 'string' && nested.length > 0) {
      out.add(nested);
    }

    collectUuidValues(nested, out);
  });
}

function remapUuidValues(value: any, uuidMap: Map<string, string>) {
  if (!value || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    value.forEach((item) => remapUuidValues(item, uuidMap));
    return;
  }

  Object.keys(value).forEach((key) => {
    const nested = value[key];

    if (typeof nested === 'string' && uuidMap.has(nested)) {
      value[key] = uuidMap.get(nested);
      return;
    }

    if (nested && typeof nested === 'object') {
      remapUuidValues(nested, uuidMap);
    }
  });
}

/**
 * Makes every measurement import instance-safe.
 *
 * The same panorama measurement can be selected/imported multiple times.
 * Source measurement JSON may contain stable UUIDs, so importing the same
 * object twice would otherwise create duplicated room/wall/space/floor UUIDs.
 */
function cloneMeasurementsForFreshImport(selectedMeasurements: PanoMeasurementModel[]): PanoMeasurementModel[] {
  return selectedMeasurements.map((measurement) => {
    const rawMeasurements = deepClonePlain((measurement as any).measurements ?? []);

    const sourceUuids = new Set<string>();
    collectUuidValues(rawMeasurements, sourceUuids);

    const uuidMap = new Map<string, string>();
    sourceUuids.forEach((uuid) => {
      if (UUID_REGEX.test(uuid)) {
        uuidMap.set(uuid, MathUtils.generateUUID());
      }
    });

    remapUuidValues(rawMeasurements, uuidMap);

    return new PanoMeasurementModel(
      (measurement as any).cameraHeight ?? 0,
      (measurement as any).floorHeight ?? (measurement as any).cameraHeight ?? 0,
      (measurement as any).description ?? '',
      rawMeasurements,
      (measurement as any).titlePicture ?? '',
      (measurement as any).compass ?? 0,
      (measurement as any).version,
      (measurement as any).region ?? ERegionCode.DE,
    );
  });
}

export const usePanoramaMeasurementActions = (
  currentBuilding: IBuildingProviderContext,
  setBuilding: (building: React.SetStateAction<IBuildingProviderContext>, forceUpdate?: boolean) => void,
  _setShowFetchingInfoModal: React.Dispatch<React.SetStateAction<boolean>>,
  initializeImportedBuildingHistory: (building: IBuildingProviderContext, cameraPositionSprites?: CameraPositionSpriteModel[]) => IBuildingProviderContext,
): IPanoramaMeasurementActions => {
  const { showSnackbar } = useSnackbar();
  const intl = useIntl();
  const { convertAnnotationsToRegion, getCameraPositionSpritesSnapshot, updateCameraPositionSprites } = useAnnotations();
  const { setRoomCompleted } = useDrawing();

  const [measurementModalOpen, setMeasurementModalOpen] = useState(false);
  const [panoMeasurements, setPanoMeasurements] = useState<PanoMeasurementModel[]>([]);
  const [measurementLoading, setMeasurementLoading] = useState(false);
  const { planModel, setPlanModel } = usePlan();

  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictingMeasurements, setConflictingMeasurements] = useState<any[]>([]);
  const [pendingImport, setPendingImport] = useState<PanoMeasurementModel[]>([]);

  const fetchPanoramaMeasurements = useCallback(
    async (plan: PlanModel, authToken: string): Promise<PanoMeasurementModel[]> => {
      try {
        const { cameraHeights, measurements, panoDescriptions, titlePictures, compasses, versions, regions } = await retrievePanoMeasurements(plan, authToken);

        const newModels = measurements.map(
          (measurement, index) =>
            new PanoMeasurementModel(
              cameraHeights[index] || 0,
              cameraHeights[index] || 0,
              panoDescriptions[index] || '',
              [measurement],
              titlePictures[index] || '',
              compasses[index] || 0,
              versions[index],
              regions[index] || ERegionCode.DE,
            ),
        );

        setPanoMeasurements(newModels);
        return newModels;
      } catch (error) {
        showSnackbar(intl.formatMessage({ id: 'PanoramaMeasurements.fetchError' }), SnackbarType.ERROR);
        return [];
      }
    },
    [showSnackbar, intl],
  );

  const executeImport = useCallback(
    async (selectedMeasurements: PanoMeasurementModel[], targetRegion: string, overridePlan?: PlanModel) => {
      setMeasurementLoading(true);

      const currentPlan = overridePlan || planModel;

      try {
        const freshMeasurements = cloneMeasurementsForFreshImport(selectedMeasurements);

        const { building, cameraOffsets } = await importAllMeasurementsToPlanJson(freshMeasurements, targetRegion, currentBuilding);

        const finalBuilding = convertBuildingAttributesToRegion(building, targetRegion);

        convertAnnotationsToRegion(targetRegion);

        if (targetRegion !== currentPlan.region) {
          setPlanModel((previousPlan) =>
            new PlanModel(
              previousPlan.name,
              previousPlan.id,
              previousPlan.kojeId,
              previousPlan.kojeLevel,
              previousPlan.eventId,
              previousPlan.dataObjectId,
              previousPlan.showroomId,
              previousPlan.appVersion,
              finalBuilding,
              previousPlan.jsonVersion,
              targetRegion,
            ),
          );
        }

        const existingCameraPositionSprites = getCameraPositionSpritesSnapshot();
        const maxExistingId = existingCameraPositionSprites.length > 0 ? Math.max(...existingCameraPositionSprites.map((sprite) => sprite.id)) : -1;
        const nextId = maxExistingId + 1;
        const importedCameraPositionSprites = cameraOffsets.map((offset, index) =>
          new CameraPositionSpriteModel(nextId + index, offset, getSpaceUuidAtPointByGeometry(finalBuilding, offset)),
        );
        const nextCameraPositionSprites = [...existingCameraPositionSprites, ...importedCameraPositionSprites];
        const snappedFinalBuilding = initializeImportedBuildingHistory(finalBuilding, nextCameraPositionSprites);

        setBuilding({
          ...snappedFinalBuilding,
          isSetByUndoRedo: true,
        }, true);

        updateCameraPositionSprites(() => nextCameraPositionSprites);

        setRoomCompleted(true);
      } catch (error) {
        console.error('Importing panorama measurements failed:', error);
        showSnackbar('Import fehlgeschlagen', SnackbarType.ERROR);
      } finally {
        setMeasurementLoading(false);
      }
    },
    [
      planModel,
      currentBuilding,
      setBuilding,
      setPlanModel,
      setRoomCompleted,
      showSnackbar,
      convertAnnotationsToRegion,
      getCameraPositionSpritesSnapshot,
      initializeImportedBuildingHistory,
      updateCameraPositionSprites,
    ],
  );

  const handleImportMeasurements = useCallback(
    async (selectedMeasurements: PanoMeasurementModel[], overridePlan?: PlanModel) => {
      setMeasurementModalOpen(false);

      const currentPlan = overridePlan || planModel;

      const hasExistingRooms = currentBuilding?.rooms && currentBuilding.rooms.length > 0;
      const uniqueImportRegions = Array.from(new Set(selectedMeasurements.map((measurement) => measurement.region).filter(Boolean)));

      if (!hasExistingRooms && uniqueImportRegions.length === 1) {
        await executeImport(selectedMeasurements, uniqueImportRegions[0], currentPlan);
        return;
      }

      const conflicts = selectedMeasurements
        .filter((measurement) => measurement.region && measurement.region !== currentPlan.region)
        .map((measurement, index) => ({
          id: index,
          description: measurement.description || `Vermessung ${index + 1}`,
          region: measurement.region,
        }));

      if (conflicts.length > 0) {
        setConflictingMeasurements(conflicts);
        setPendingImport(selectedMeasurements);
        setConflictModalOpen(true);
        return;
      }

      await executeImport(selectedMeasurements, currentPlan.region, currentPlan);
    },
    [planModel, executeImport, currentBuilding],
  );

  const handleConfirmConflict = async (decision: string) => {
    setConflictModalOpen(false);

    const targetRegion = decision === 'PLAN' ? planModel.region : decision;

    await executeImport(pendingImport, targetRegion);
    setPendingImport([]);
  };

  const handleCancelConflict = () => {
    setConflictModalOpen(false);
    setPendingImport([]);
  };

  return {
    fetchPanoramaMeasurements,
    handleImportMeasurements,
    measurementModalOpen,
    setMeasurementModalOpen,
    panoMeasurements,
    setPanoMeasurements,
    measurementLoading,
    setMeasurementLoading,
    conflictModalOpen,
    conflictingMeasurements,
    handleConfirmConflict,
    handleCancelConflict,
  };
};
